import { useCallback, useEffect, useRef, useState } from "react";

const useCountDown = (
	initialTime = 60000,
	shouldStart?: boolean,
	interval = 1000,
) => {
	const [timeLeft, setTimeLeft] = useState(initialTime);
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const start = useCallback(
		(newTime?: number) => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
			setTimeLeft(typeof newTime === "number" ? newTime : initialTime);
			intervalRef.current = setInterval(() => {
				setTimeLeft((prevTime) => prevTime - interval);
			}, interval);
		},
		[initialTime, interval],
	);

	const pause = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
	}, []);

	const resume = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(() => {
			setTimeLeft((prevTime) => prevTime - interval);
		}, interval);
	}, [interval]);

	const reset = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		setTimeLeft(initialTime);
	}, [initialTime]);

	useEffect(() => {
		if (shouldStart) {
			start();
		}
		return pause;
	}, [start, pause, shouldStart]);

	const totalSeconds = Math.floor(timeLeft / 1000);
	const absTotalSeconds = Math.abs(totalSeconds);
	const seconds = absTotalSeconds % 60;
	const minutes = Math.floor(absTotalSeconds / 60) % 60;
	const hours = Math.floor(absTotalSeconds / 3600);

	return {
		hours: hours.toString(),
		minutes: minutes.toString().padStart(2, "0"),
		seconds: seconds.toString().padStart(2, "0"),
		isNegative: totalSeconds < 0,
		start,
		pause,
		resume,
		reset,
	};
};

export default useCountDown;
